import { useMemo } from "react"
import {
  Avatar,
  Box,
  Button,
  Typography,
  useTheme,
  CardMedia,
  Card,
} from "@mui/material"
import size from "lodash/size"
import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { useMobileUrl } from "hooks/useMobileUrl"
import { useImageModal } from "hooks/imageModal/useImageModal"
import { HorizontalScrollPane } from "components/HorizontalScrollPane/HorizontalScrollPane"
import { FeatureLabel } from "./FeatureLabel"
import { CloseIconButton } from "components/Button/CloseIconButton"
import { QRCode } from "components/QRCode"
import { CloseIcon } from "../../icons"
import { StoreIcon } from "components/icons"
import { ImageCarouselModal } from "components/Image"

export const FeatureCard = ({
  open,
  onClose,
  directionButtonProps = {},
  promotionButtonProps = {},
  loading = true,
  data = {},
  onNavigate = () => {},
  onClickPromotionButton = () => {},
  categoryIconProp = {},
  childrenProps = {},
  isNavigatable = true,
  ...props
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { mobileUrl } = useMobileUrl()
  const {
    logo,
    category,
    name,
    hours,
    gallery,
    description,
    location,
    promotions,
    phone,
  } = data
  const galleryImageLength = size(gallery)
  const galleryUrls = useMemo(
    () => (size(gallery) > 0 ? gallery?.map((v) => v?.url) : []),
    [gallery]
  )
  const { handleImage, openModal, activeImage, handleClose } = useImageModal()

  const {
    root,
    primarySection,
    secondarySection,
    header,
    content,
    avatar,
    imageListContainer,
    imageList,
    imageItem,
    actionContainer,
    listContainer,
    qrCodeContainer,
    closeButton,
    horizontalScrollPane,
    operationTimeLabel,
  } = theme.components[`FeatureCard`]?.styleOverrides || {}

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.closeIcon) || <CloseIcon />,
    [theme]
  )

  return (
    <>
      <Card sx={root} {...childrenProps}>
        <CloseIconButton
          sx={closeButton}
          icon={closeIcon}
          onClick={onClose}
          size="large"
        />
        <Box sx={primarySection}>
          <Box sx={header}>
            <Avatar variant="circular-logo" sx={avatar} src={logo}>
              {getIcon(categoryIconProp, null, {
                imageIconProps: {
                  solidColorFilterId: "placeDetail_avatar_filter",
                },
              }) || <StoreIcon />}
            </Avatar>
            <Typography gutterBottom variant="h5">
              {t(name)}
            </Typography>
          </Box>
          {description && (
            <Box sx={content}>
              <Typography variant="body2" color="text.secondary">
                {t(description)}
              </Typography>
            </Box>
          )}
          {galleryImageLength > 0 && (
            <Box sx={imageListContainer}>
              <HorizontalScrollPane
                sx={horizontalScrollPane}
                stackProps={{
                  sx: {
                    ...imageList,
                    justifyContent:
                      galleryImageLength <= 2 ? "center" : "flex-start",
                  },
                }}
                simpleBarProps={{ autoHide: false }}
              >
                {gallery.map(({ name, url }, idx) => (
                  <CardMedia
                    key={`${name}-${idx}`}
                    sx={imageItem}
                    image={url}
                    title={name}
                    onClick={() => handleImage(url)}
                  />
                ))}
              </HorizontalScrollPane>
            </Box>
          )}
          <Box sx={actionContainer}>
            {promotions.length > 0 && (
              <Button
                variant="outlined"
                onClick={onClickPromotionButton}
                {...promotionButtonProps}
              >
                {t("Promotion")}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={onNavigate}
              {...directionButtonProps}
              sx={{ visibility: isNavigatable ? "visible" : "hidden" }}
            >
              {t("Get direction")}
            </Button>
          </Box>
        </Box>
        <Box sx={secondarySection}>
          <Box sx={listContainer}>
            <FeatureLabel label={t("Location")} value={t(location)} />
            <FeatureLabel label={t("Category")} value={t(category)} />
            <FeatureLabel
              label={t("Operation Time")}
              value={hours || "-"}
              sx={operationTimeLabel}
            />
            {phone && <FeatureLabel label={t("Phone")} value={phone || "-"} />}
            {mobileUrl && (
              <QRCode
                sx={qrCodeContainer}
                url={mobileUrl}
                caption={t("Scan Here")}
                secondaryCaption={t("For Mobile")}
              />
            )}
          </Box>
        </Box>
      </Card>
      <ImageCarouselModal
        activeSrc={activeImage}
        open={openModal}
        imageSrcs={galleryUrls}
        onClose={handleClose}
      />
    </>
  )
}
